import { formatDate, formatDateToTime, numberWithCommas } from "@utils/helpers";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { DashboardLayout } from "@layouts/DashboardLayout";
import {
  clientReceivedDoc,
  fetchUserClientDocument,
  getComments,
  getSingleInterestDetails,
  getUserInterests,
  getUserPurchasedInterests,
} from "@services/krentDeveloper";
import ImageCarousel from "@pages/Developers/Projects/image-carousel";
import BookInspection from "components/Developer/Purchases/book-inspection";
import { toast, ToastContainer } from "react-toastify";
import { PropertyInterest } from "@utils/constants";

const PURCHASE_STEPS = [
  "Request accepted",
  "Inspection confirmed",
  "Payment made",
  "Documentation process",
  "Documentation received",
] as const;

const getCurrentStep = (item: any) => {
  if (item?.purchaseDocument && Object.keys(item.purchaseDocument).length > 0) {
    return 5;
  }
  if (
    item?.paymentStatus === "confirmed" &&
    item?.documentationStatus === "Interest Request Process"
  ) {
    return 4;
  }
  if (item?.paymentStatus === "confirmed") {
    return 3;
  }
  if (item?.isBooked && item?.inspected) {
    return 2;
  }
  if (item?.status === "Accepted" || item?.status === "Purchase-Request") {
    return 1;
  }
  return 0;
};

const UserPurchasedPropertyDetails = () => {
  const { id } = useParams();
  const [propertyData, setPropertyData] = useState<any>(null);
  const [devComments, setDevComments] = useState<any[]>([]);
  const [devClientDocument, setDevClientDocument] = useState<any>(null);
  const [interests, setInterests] = useState<PropertyInterest[]>([]);

  console.log(devClientDocument, interests);
  const fetchProjectDetails = useCallback(async () => {
    const controller = new AbortController();
    try {
      const response = await getSingleInterestDetails(id!, controller.signal);
      setPropertyData(response.data || null);
    } catch (error) {
      if (error instanceof Error && error.name !== "AbortError") {
        console.error("Failed to fetch project details:", error);
        toast.error("Failed to load property details");
      }
    }
    return () => controller.abort();
  }, [id]);

  const fetchRelatedData = useCallback(async () => {
    if (!propertyData?._id) return;

    const controller = new AbortController();
    try {
      const [commentsResponse, documentResponse, purchasesResponse] =
        await Promise.all([
          getComments(propertyData._id, controller.signal),
          fetchUserClientDocument(propertyData._id, controller.signal),
          getUserPurchasedInterests(controller.signal),
        ]);

      setDevComments(commentsResponse.data);
      setDevClientDocument(documentResponse.data);
      const updatedPurchase = purchasesResponse?.data?.find(
        (purchase: any) => purchase._id === propertyData._id
      );
      if (updatedPurchase) {
        setPropertyData(updatedPurchase);
      }
    } catch (error) {
      if (error instanceof Error && error.name !== "AbortError") {
        console.error("Error fetching related data:", error);
        toast.error("Failed to load some property information");
      }
    }
    return () => controller.abort();
  }, [propertyData?._id]);

  const fetchInterests = useCallback(async () => {
    const controller = new AbortController();
    try {
      const response = await getUserInterests(controller.signal);
      setInterests(response.data);
    } catch (error) {
      if (error instanceof Error && error.name !== "AbortError") {
        console.error("Error fetching interests:", error);
      }
    }
    return () => controller.abort();
  }, []);

  const handleDocReceived = async () => {
    if (!propertyData?._id) return;

    const controller = new AbortController();
    try {
      await clientReceivedDoc(propertyData._id, controller.signal);
      toast.success("Document receipt confirmed successfully");
      await fetchRelatedData();
    } catch (error) {
      console.error("Error confirming document receipt:", error);
      toast.error("Failed to confirm document receipt");
    }
    return () => controller.abort();
  };

  useEffect(() => {
    fetchProjectDetails();
  }, [fetchProjectDetails]);

  useEffect(() => {
    fetchRelatedData();
  }, [fetchRelatedData]);

  useEffect(() => {
    fetchInterests();
  }, [fetchInterests]);

  if (!propertyData) {
    return <div>No property details found</div>;
  }
  return (
    <DashboardLayout>
      <ToastContainer limit={1} />
      <div className="px-10 mx-auto my-4">
        <div className="mt-2">
          <h6 className="my-4">Property details</h6>
          <div className="flex justify-between items-center">
            <div className="flex gap-2 items-center">
              <i className="fa-solid fa-angle-left" />
              <a href="/purchases" className="text-link-secondary fs-16">
                Back to purchases
              </a>
            </div>
          </div>
        </div>

        <div className="flexed-item my-10 gap-4 items-start align-items-center">
          <ImageCarousel images={propertyData.property?.otherImages} />

          <div className="w-full">
            <div
              className="stepper-container"
              style={
                {
                  "--total-steps": 5,
                  "--current-step": getCurrentStep(propertyData),
                } as React.CSSProperties
              }
            >
              {PURCHASE_STEPS.map((step, index) => (
                <div key={index} className="w-full" style={{ height: "80px" }}>
                  <div
                    className={`step-item ${
                      index < getCurrentStep(propertyData) ? "active" : ""
                    }`}
                  >
                    <div className="step-number">{index + 1}</div>
                    {index < 4 && <div className="step-line" />}
                  </div>
                  <div className="mt-1">{step}</div>
                </div>
              ))}
            </div>

            <h5
              className={`fw-bold w-max rounded-50 px-3 fs-12 mb-3 ${
                propertyData.property?.propertyType === "bungalow"
                  ? "previous"
                  : propertyData.property?.propertyType === "flats"
                  ? "current"
                  : "future"
              }`}
            >
              <span className="capitalize">
                {propertyData.property?.propertyType}
              </span>
            </h5>

            <h4 className="my-3">{propertyData.property?.propertyName}</h4>
            <h4 className="my-3">
              <span className="uppercase">
                {propertyData.property?.currency}
              </span>
              &nbsp;
              {numberWithCommas(propertyData.property?.price)}
            </h4>

            <p className="gap-2 flex items-center my-3">
              <i className="fa fa-location-dot text-primary fa-regular" />
              {propertyData.property?.address}
            </p>

            <div className="flex gap-20 items-center my-2">
              <div>
                <span className="fs-14 fw-bold">
                  <i className="fa-light fa-bed-front text-primary" />
                  &nbsp;
                  {propertyData?.property?.totalUnits || 0}&nbsp;total units
                </span>
              </div>
              <div>
                <span className="fs-14 fw-bold">
                  <i className="fa-light fa-bath text-primary" />
                  &nbsp;
                  {propertyData.property?.availableUnits || 0}&nbsp;available
                  units
                </span>
              </div>
            </div>

            <div className="flex gap-20 items-center">
              <div>
                <span className="fs-14 fw-bold">
                  <i className="fa-light fa-bed-front text-primary" />
                  &nbsp;
                  {propertyData.property?.numberOfBedrooms || 0}&nbsp;bedrooms
                </span>
              </div>
              <div>
                <span className="fs-14 fw-bold">
                  <i className="fa-light fa-bath text-primary" />
                  &nbsp;
                  {propertyData.property?.numberOfBathrooms || 0}&nbsp;bathrooms
                </span>
              </div>
            </div>

            <div className="flex gap-4 w-80 mt-4">
              {(propertyData.initiationDate || propertyData?.createdAt) && (
                <div>
                  <h6 className="fs-14">Initiation date</h6>
                  <p className="text-center">
                    {formatDate(
                      propertyData.initiationDate || propertyData?.createdAt
                    )}
                  </p>
                </div>
              )}
            </div>

            <div className="my-2">
              {propertyData.purchaseDocument &&
                Object.keys(propertyData.purchaseDocument).length > 0 && (
                  <div className="text-primary">
                    <h6>
                      {propertyData.documentationStatus === "Client Received" &&
                        "Download"}
                      Document from Developer
                    </h6>
                    <div>
                      {Object.entries(propertyData.purchaseDocument).map(
                        ([key, value]) => {
                          const isDownloadable =
                            propertyData.documentationStatus ===
                            "Client Received";
                          const displayValue =
                            String(value).length > 20
                              ? `${String(value).slice(0, 20)}...`
                              : String(value);

                          return (
                            <div
                              key={key}
                              className="flex items-center gap-4 my-2"
                            >
                              <h6 className="capitalize fs-14 w-30">{key}:</h6>
                              {isDownloadable ? (
                                <a
                                  href={String(value)}
                                  download
                                  className="rounded-10 p-2 w-full flex justify-between border-green text-primary"
                                >
                                  <p>{displayValue}</p>
                                  <i className="fa-regular fa-download text-primary p-1" />
                                </a>
                              ) : (
                                <button
                                  className="rounded-10 p-2 w-full flex justify-between disable-download"
                                  disabled
                                >
                                  <p>{displayValue}</p>
                                </button>
                              )}
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}

              {propertyData.purchaseDocument &&
                Object.keys(propertyData.purchaseDocument).length > 0 &&
                propertyData.documentationStatus !== "Client Received" && (
                  <button
                    onClick={handleDocReceived}
                    className="bg-white border rounded-10 bg-primary text-white p-2 px-4 font-bold"
                  >
                    Click to confirm that developer document is visible
                  </button>
                )}

              {!propertyData.purchaseRequestReceipt && (
                <div>
                  <h6 className="fs-12">
                    Please proceed to make transfer payment
                  </h6>
                  <p>
                    If you have not made payment, kindly make transfer to this
                    details
                  </p>
                  <div className="my-3">
                    <p className="fs-16">
                      <span className="fw-bold">Acc name:&nbsp;</span>
                      Krent space
                    </p>
                    <p className="fs-16">
                      <span className="fw-bold">Bank name:&nbsp;</span>
                      Wema bank
                    </p>
                    <p className="fs-16">
                      <span className="fw-bold">Acc number:&nbsp;</span>
                      0123456789
                    </p>
                  </div>
                </div>
              )}
            </div>

            {!propertyData.isBooked && (
              <div className="bg-primary-light rounded-2 p-2 fs-14 flex gap-2">
                <i className="fa-regular fa-info-circle mt-1" />
                <p>
                  Your interest has been communicated to the agent. We are
                  waiting for a confirmation on the availablity of the property.{" "}
                </p>
              </div>
            )}

            {propertyData.purchaseDocument &&
              Object.keys(propertyData.purchaseDocument).length <= 0 && (
                <div className="my-4">
                  <BookInspection item={propertyData} />
                </div>
              )}
          </div>
        </div>

        <div className="flexed-item gap-3">
          <div className="justify-between border rounded-4 p-3 w-full">
            <h5>Developer details</h5>
            <div className="flexed-item py-2 gap-4">
              <div className="w-full">
                <p className="capitalize">
                  {propertyData.developer.firstname.toLowerCase()}&nbsp;
                  {propertyData.developer.lastname.toLowerCase()}
                </p>
                <p>Developer</p>
              </div>
              <div className="w-full">
                <div className="flex gap-4">
                  <h6>Developer email</h6>
                  <p>{propertyData.developer.email}</p>
                </div>
                <div className="flex gap-4">
                  <h6>Developer phone number</h6>
                  <p>{propertyData.developer.phone}</p>
                </div>
              </div>
            </div>

            {devComments.length > 0 && (
              <>
                <hr />
                <div>
                  <h6 className="pb-2">Developer comments</h6>
                  {devComments.map((comment: any, idx: number) => (
                    <div key={idx} className="w-15">
                      <div className="border p-2 rounded-10">
                        <p>{comment.content}</p>
                      </div>
                      <p className="fs-10 py-2 w-max ml-auto">
                        {formatDateToTime(comment.timestamp)}
                      </p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="justify-between border rounded-4 p-3 w-full mt-4 mt-md-0">
            <h5>Agent details</h5>
            <div className="flexed-item  py-2 gap-4">
              <div className="w-full">
                <p className="capitalize">
                  {propertyData.agent.firstname.toLowerCase()}&nbsp;
                  {propertyData.agent.lastname.toLowerCase()}&nbsp;
                </p>
                <p>Agent</p>
              </div>
              <div className="w-full">
                <div className="flex gap-4">
                  <h6>Email</h6>
                  <p>{propertyData.agent.email}</p>
                </div>
                <div className="flex gap-4">
                  <h6>Phone number</h6>
                  <p>{propertyData.agent.phone}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default UserPurchasedPropertyDetails;
